// Native
import { useEffect, useState } from "react"
import { useLocation, useNavigate } from "react-router"
import OneSignal from "react-onesignal"

// Components
import {
    Avatar, Badge, Box, Divider, Drawer, DrawerBody, DrawerCloseButton, DrawerContent,
    DrawerHeader, DrawerOverlay, Flex, HStack, Image, Input,
    Popover, PopoverArrow, PopoverBody, PopoverContent, PopoverFooter,
    PopoverHeader, PopoverTrigger, Progress,
    Stack, Tag, Text,
    useColorMode, useColorModeValue, useDisclosure
} from "@chakra-ui/react"
import { Icon, IconButton } from "components"

// Config
import { API } from "config"

// Hooks
import { useUser, useStockAlerts } from "hooks"

// Screens
import { Notification, Search } from "screens"

// ── Layout constants ───────────────────────────────────────────────────────────
export const SIDEBAR_W = "220px"
export const TOPBAR_H  = "60px"

// ── Nav sections ───────────────────────────────────────────────────────────────
const NAV_SECTIONS = [
    {
        label: null,
        items: [{ path: "/", icon: "home", label: "Dashboard", role: null }],
    },
    {
        label: "Gestão",
        items: [
            { path: "/consultants",  icon: "consultants",  label: "Consultores",  role: "consultants_view"      },
            { path: "/distributors", icon: "distributors", label: "Núcleos",      role: "distributors_view"     },
            { path: "/orders",       icon: "orders",       label: "Pedidos",      role: "orders_view"           },
            { path: "/sales",        icon: "transfer",     label: "Venda Direta", role: "distributors_transfer" },
        ],
    },
    {
        label: "Financeiro",
        items: [
            { path: "/withdraws", icon: "financial", label: "Saques", role: "withdraws_manage" },
            { path: "/fiscal",    icon: "fiscal",    label: "Fiscal",  role: "fiscal_manage"   },
        ],
    },
    {
        label: "Conteúdo",
        items: [
            { path: "/products", icon: "products", label: "Produtos",   role: "products_view" },
            { path: "/reports",  icon: "reports",  label: "Relatórios", role: "reports_view"  },
        ],
    },
    {
        label: "Estoque",
        items: [
            { path: "/stock/invoices",      icon: "invoices",      label: "NF-e / Entradas",    role: "stock_invoices"          },
            { path: "/stock/inventory",     icon: "stock",         label: "Inventário",          role: "stock_manage"            },
            { path: "/stock/movements",     icon: "movements",     label: "Movimentações",       role: "stock_movements"         },
            { path: "/stock/losses",        icon: "losses",        label: "Perdas",              role: "stock_losses"            },
            { path: "/stock/traceability",  icon: "traceability",  label: "Rastreabilidade",     role: "stock_movements"         },
            { path: "/stock/conference",    icon: "roles",         label: "Conferência",         role: "stock_conference"        },
            { path: "/stock/adjustments",   icon: "edit",          label: "Ajuste Manual",       role: "stock_manual_adjustment" },
            { path: "/stock/transfers",       icon: "transfer",      label: "Transferências",      role: "stock_manage"            },
            { path: "/stock/alerts",          icon: "stock",         label: "Alertas de Estoque",  role: "stock_manage"            },
            { path: "/stock/reconciliation",  icon: "invoices",      label: "Conciliações",        role: "stock_invoices"          },
        ],
    },
    {
        label: "Operações",
        items: [
            { path: "/tools",     icon: "tools",     label: "Ferramentas", role: "tools"             },
            { path: "/warehouse", icon: "warehouse", label: "Expedição",   role: "warehouse"         },
            { path: "/employees", icon: "employees", label: "Funcionários", role: "employees_manage" },
            { path: "/options",   icon: "settings",  label: "Opções",       role: "options_manage"   },
        ],
    },
]

// ── Painel de alertas de estoque mínimo ───────────────────────────────────────
function StockAlertPanel({ data = [], count = 0, compact = false }) {
    const { isOpen, onOpen, onClose } = useDisclosure()
    const [search, setSearch] = useState('')

    const popBg      = useColorModeValue("white",        "gray.800")
    const popBorder  = useColorModeValue("gray.200",     "gray.600")
    const headBg     = useColorModeValue("orange.50",    "gray.750")
    const cardBorder = useColorModeValue("gray.100",     "gray.700")
    const labelC     = useColorModeValue("gray.500",     "gray.400")
    const hoverBg    = useColorModeValue("gray.50",      "gray.750")
    const trigBg     = useColorModeValue("gray.50",      "whiteAlpha.50")
    const borderC    = useColorModeValue("gray.100",     "gray.700")
    const timeMuted  = useColorModeValue("gray.500",     "gray.400")
    const trigHover  = useColorModeValue("orange.50",    "whiteAlpha.100")
    const searchBg   = useColorModeValue("gray.50",      "gray.700")

    if (!count) return null

    const filteredData = search.trim()
        ? data
            .map(p => ({
                ...p,
                nuclei: p.nuclei.filter(n =>
                    n.distributor_name.toLowerCase().includes(search.toLowerCase())
                ),
            }))
            .filter(p => p.nuclei.length > 0)
        : data

    const compactTrigger = (
        <Box
            as="button"
            position="relative"
            display="inline-flex"
            onClick={isOpen ? onClose : onOpen}
            aria-label="Alertas de estoque"
        >
            <Flex
                align="center"
                justify="center"
                w="34px"
                h="34px"
                borderRadius="lg"
                color="orange.400"
                bg={isOpen ? "orange.50" : "transparent"}
                _dark={{ bg: isOpen ? "whiteAlpha.100" : "transparent" }}
                _hover={{ bg: "orange.50", _dark: { bg: "whiteAlpha.100" } }}
                transition="all 0.15s"
            >
                <Icon name="stock" boxSize={4} />
            </Flex>
            <Box
                position="absolute"
                top="-3px"
                right="-3px"
                bg="orange.400"
                color="white"
                borderRadius="full"
                fontSize="2xs"
                fontWeight="bold"
                minW="16px"
                h="16px"
                display="flex"
                alignItems="center"
                justifyContent="center"
                px="3px"
                lineHeight="1"
                pointerEvents="none"
                border="2px solid"
                borderColor={popBg}
            >
                {count > 9 ? "9+" : count}
            </Box>
        </Box>
    )

    const fullTrigger = (
        <HStack
            as="button"
            spacing={2}
            bg={trigBg}
            px={3}
            py={1.5}
            borderRadius="lg"
            border="1px solid"
            borderColor={borderC}
            flexShrink={0}
            cursor="pointer"
            _hover={{ bg: trigHover, borderColor: "orange.200" }}
            _dark={{ _hover: { borderColor: "orange.700" } }}
            transition="all 0.15s"
            onClick={isOpen ? onClose : onOpen}
            aria-label="Alertas de estoque"
        >
            <Box position="relative" display="inline-flex">
                <Icon name="stock" boxSize={4} color="orange.400" />
                <Box
                    position="absolute"
                    top="-5px"
                    right="-6px"
                    bg="orange.400"
                    color="white"
                    borderRadius="full"
                    fontSize="2xs"
                    fontWeight="bold"
                    minW="14px"
                    h="14px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    px="2px"
                    lineHeight="1"
                    pointerEvents="none"
                    border="1.5px solid"
                    borderColor={popBg}
                >
                    {count > 9 ? "9+" : count}
                </Box>
            </Box>
            <Box lineHeight={1.2} textAlign="left">
                <Text fontSize="xs" fontWeight="semibold" noOfLines={1}>
                    Alerta de Estoque
                </Text>
                <Text fontSize="2xs" color={timeMuted} noOfLines={1}>
                    {count} produto{count !== 1 ? "s" : ""} abaixo do mínimo
                </Text>
            </Box>
        </HStack>
    )

    return (
        <Popover isOpen={isOpen} onClose={onClose} placement="bottom-end" isLazy>
            <PopoverTrigger>
                {compact ? compactTrigger : fullTrigger}
            </PopoverTrigger>

            <PopoverContent
                w="400px"
                bg={popBg}
                borderColor={popBorder}
                boxShadow="0 8px 30px rgba(0,0,0,0.12)"
                borderRadius="xl"
                overflow="hidden"
                _focus={{ outline: "none" }}
            >
                <PopoverArrow bg={popBg} borderColor={popBorder} />

                {/* Cabeçalho */}
                <PopoverHeader
                    bg={headBg}
                    borderBottomColor={popBorder}
                    px={4}
                    py={3}
                    _dark={{ bg: "gray.750" }}
                >
                    <Flex align="center" justify="space-between">
                        <HStack spacing={2}>
                            <Icon name="stock" color="orange.400" boxSize={4} />
                            <Text fontSize="sm" fontWeight="bold" color="orange.600" _dark={{ color: "orange.300" }}>
                                Alerta de Estoque
                            </Text>
                        </HStack>
                        <Badge colorScheme="orange" borderRadius="full" fontSize="2xs" px={2}>
                            {count} produto{count !== 1 ? "s" : ""}
                        </Badge>
                    </Flex>
                </PopoverHeader>

                {/* Campo de busca */}
                <Box px={3} py={2} borderBottomWidth={1} borderColor={popBorder} bg={popBg}>
                    <Input
                        size="sm"
                        placeholder="Filtrar por núcleo..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        borderRadius="lg"
                        bg={searchBg}
                        _focus={{ borderColor: "orange.300", boxShadow: "0 0 0 1px var(--chakra-colors-orange-300)" }}
                    />
                </Box>

                {/* Lista de produtos */}
                <PopoverBody p={0} maxH="360px" overflowY="auto">
                    {filteredData.length === 0 ? (
                        <Flex direction="column" align="center" py={8} gap={2} color={labelC}>
                            <Icon name="search" boxSize={5} />
                            <Text fontSize="sm">Nenhum núcleo encontrado</Text>
                        </Flex>
                    ) : filteredData.map((product, pi) => (
                        <Box
                            key={product.id}
                            borderBottomWidth={pi < filteredData.length - 1 ? 1 : 0}
                            borderColor={cardBorder}
                        >
                            <Box px={4} pt={3} pb={1}>
                                <Flex align="center" justify="space-between" mb={2}>
                                    <Text fontSize="sm" fontWeight="bold" noOfLines={1} flex={1} mr={2}>
                                        {product.name}
                                    </Text>
                                    <Flex
                                        align="center"
                                        gap={1}
                                        bg="orange.50"
                                        _dark={{ bg: "gray.700" }}
                                        px={2}
                                        py="2px"
                                        borderRadius="full"
                                        flexShrink={0}
                                    >
                                        <Text fontSize="2xs" fontWeight="semibold" color="orange.600" _dark={{ color: "orange.300" }}>
                                            Mín: {product.min_stock} un.
                                        </Text>
                                    </Flex>
                                </Flex>

                                {product.nuclei.map((nucleus, ni) => {
                                    const pct      = Math.max(0, Math.min(100, (nucleus.available / product.min_stock) * 100))
                                    const critical = nucleus.available / product.min_stock < 0.3
                                    const scheme   = critical ? "red" : "orange"
                                    return (
                                        <Box
                                            key={nucleus.distributor_id}
                                            py={2}
                                            borderTopWidth={ni > 0 ? 1 : 0}
                                            borderColor={cardBorder}
                                            borderStyle="dashed"
                                            _hover={{ bg: hoverBg }}
                                            transition="background 0.1s"
                                            borderRadius="md"
                                            px={1}
                                        >
                                            <Flex justify="space-between" align="center" mb={1.5}>
                                                <Text fontSize="xs" color={labelC} noOfLines={1} flex={1} mr={2}>
                                                    {nucleus.distributor_name}
                                                </Text>
                                                <HStack spacing={1} flexShrink={0}>
                                                    <Text fontSize="xs" fontWeight="bold"
                                                        color={critical ? "red.500" : "orange.500"}>
                                                        {nucleus.available}
                                                    </Text>
                                                    <Text fontSize="2xs" color={labelC}>/ {product.min_stock} un.</Text>
                                                    <Badge colorScheme={scheme} fontSize="2xs" variant="subtle" borderRadius="full">
                                                        -{nucleus.deficit}
                                                    </Badge>
                                                </HStack>
                                            </Flex>
                                            <Progress
                                                value={pct}
                                                size="xs"
                                                colorScheme={scheme}
                                                bg="gray.100"
                                                _dark={{ bg: "gray.600" }}
                                                borderRadius="full"
                                            />
                                        </Box>
                                    )
                                })}
                            </Box>
                            <Box pb={2} />
                        </Box>
                    ))}
                </PopoverBody>

                <PopoverFooter borderTopColor={popBorder} px={4} py={2.5}>
                    <Text fontSize="2xs" color={labelC} textAlign="center">
                        Configure o estoque mínimo na ficha de cada produto
                    </Text>
                </PopoverFooter>
            </PopoverContent>
        </Popover>
    )
}

// ── Sidebar nav item ───────────────────────────────────────────────────────────
function NavItem({ icon, label, active, badge, onClick }) {
    const hoverBg   = useColorModeValue("gray.100",  "whiteAlpha.50")
    const activeBg  = useColorModeValue("cyan.50",   "whiteAlpha.100")
    const textColor = useColorModeValue("gray.600",  "gray.300")
    const accent    = "#008AAC"

    return (
        <HStack
            px={4}
            py={2}
            mx={2}
            borderRadius="lg"
            cursor="pointer"
            bg={active ? activeBg : "transparent"}
            _hover={{ bg: active ? activeBg : hoverBg }}
            onClick={onClick}
            transition="all 0.15s"
            spacing={3}
            borderLeftWidth="3px"
            borderLeftColor={active ? accent : "transparent"}
            pl={active ? 3 : 4}
        >
            <Icon name={icon} color={active ? accent : textColor} boxSize={4} flexShrink={0} />
            <Text
                fontSize="sm"
                fontWeight={active ? "semibold" : "normal"}
                color={active ? accent : textColor}
                noOfLines={1}
                flex={1}
            >
                {label}
            </Text>
            {badge > 0 && (
                <Box
                    bg="orange.400"
                    color="white"
                    borderRadius="full"
                    fontSize="2xs"
                    fontWeight="bold"
                    minW="18px"
                    h="18px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    px={1}
                    flexShrink={0}
                >
                    {badge > 99 ? "99+" : badge}
                </Box>
            )}
        </HStack>
    )
}

// ── Header (TopBar + Sidebar) ──────────────────────────────────────────────────
export default function Header() {
    const navigate  = useNavigate()
    const location  = useLocation()
    const user        = useUser()
    const stockAlerts = useStockAlerts()
    const alertCount  = stockAlerts.data?.count ?? 0
    const alertData   = stockAlerts.data?.data  ?? []
    const { colorMode, toggleColorMode } = useColorMode()
    const [loading, setLoading]   = useState(false)
    const [time, setTime]         = useState(() => new Date())
    const search       = useDisclosure()
    const notification = useDisclosure()
    const mobileMenu   = useDisclosure()
    const optedIn      = OneSignal.User.PushSubscription.optedIn

    // Live clock — updates every minute
    useEffect(() => {
        const id = setInterval(() => setTime(new Date()), 60_000)
        return () => clearInterval(id)
    }, [])

    // Global Ctrl+K / Cmd+K shortcut
    useEffect(() => {
        const handleKey = (e) => {
            if ((e.ctrlKey || e.metaKey) && e.key === "k") {
                e.preventDefault()
                search.onOpen()
            }
        }
        document.addEventListener("keydown", handleKey)
        return () => document.removeEventListener("keydown", handleKey)
    }, [search])

    // Colors
    const barBg         = useColorModeValue("white",       "gray.800")
    const borderColor   = useColorModeValue("gray.100",    "gray.700")
    const logoColor     = useColorModeValue("gray.800",    "white")
    const searchBg      = useColorModeValue("gray.50",     "whiteAlpha.50")
    const searchBorder  = useColorModeValue("gray.200",    "whiteAlpha.100")
    const searchMuted   = useColorModeValue("gray.400",    "gray.500")
    const kbdBg         = useColorModeValue("gray.100",    "whiteAlpha.100")
    const timeMuted     = useColorModeValue("gray.500",    "gray.400")
    const sectionColor  = useColorModeValue("gray.400",    "gray.500")
    const userBg        = useColorModeValue("gray.50",     "whiteAlpha.50")

    // Role label
    const roleLabel = user.data?.roles?.includes("*")
        ? "Administrador"
        : user.data?.roles?.length > 0 ? "Operador" : ""

    // Role filter for nav
    const hasRole = (role) =>
        !role || !!(user.data?.roles?.includes(role) || user.data?.roles?.includes("*"))

    // Active route
    const isActive = (path) =>
        path === "/" ? location.pathname === "/" : location.pathname.startsWith(path)

    const timeStr = time.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })

    const handleLogout = async () => {
        if (!window.confirm("Tem certeza que deseja desconectar?")) return
        setLoading(true)
        try { await API.get("/employees/logout") } catch {}
        try { await OneSignal.logout() } catch {}
        try {
            localStorage.removeItem("@token")
            localStorage.removeItem("@user")
            localStorage.removeItem("@onesignal")
            sessionStorage.clear()
            window.location.reload()
        } catch {} finally { setLoading(false) }
    }

    const oneSignalAuth = async () => {
        try {
            await OneSignal.login(user.data?.id)
            localStorage.setItem("@onesignal", 1)
        } catch {}
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { if (!localStorage.getItem("@onesignal")) oneSignalAuth() }, [])

    return (
        <>
            {/* ── Top Bar ───────────────────────────────────────────────────── */}
            <Box
                as="header"
                position="fixed"
                top={0}
                left={0}
                right={0}
                h={TOPBAR_H}
                zIndex={20}
                bg={barBg}
                borderBottomWidth={1}
                borderBottomColor={borderColor}
                boxShadow="0 1px 4px rgba(0,0,0,0.04)"
            >
                <Flex h="full" align="center" px={4} gap={3}>

                    {/* Hamburger — mobile only */}
                    <IconButton
                        icon="menu"
                        size="sm"
                        variant="ghost"
                        label="Menu"
                        display={{ base: "flex", md: "none" }}
                        onClick={mobileMenu.onOpen}
                    />

                    {/* Logo */}
                    <HStack spacing={2} w={{ base: "auto", md: SIDEBAR_W }} flexShrink={0} pr={2}>
                        <Image
                            src="/icon.png"
                            h="28px"
                            cursor="pointer"
                            onClick={() => navigate("/")}
                        />
                        <Text fontWeight="bold" fontSize="sm" color={logoColor} letterSpacing="tight">
                            Bonífica
                        </Text>
                    </HStack>

                    {/* Search bar — desktop only */}
                    <HStack
                        flex={1}
                        maxW="400px"
                        display={{ base: "none", md: "flex" }}
                        bg={searchBg}
                        border="1px solid"
                        borderColor={searchBorder}
                        borderRadius="lg"
                        px={3}
                        py="7px"
                        cursor="pointer"
                        _hover={{ borderColor: "#008AAC", boxShadow: "0 0 0 1px #008AAC22" }}
                        transition="all 0.15s"
                        onClick={search.onOpen}
                        spacing={2}
                    >
                        <Icon name="search" color={searchMuted} boxSize={4} flexShrink={0} />
                        <Text fontSize="sm" color={searchMuted} userSelect="none" flex={1}>
                            Buscar pedidos, consultores...
                        </Text>
                        <HStack spacing={1} flexShrink={0}>
                            <Text fontSize="2xs" bg={kbdBg} color={searchMuted} px={1.5} py="2px" borderRadius="md" fontFamily="mono">
                                ⌘K
                            </Text>
                        </HStack>
                    </HStack>

                    <Box flex={1} />

                    {/* Mobile actions: alerts + dark mode + logout */}
                    <HStack display={{ base: "flex", md: "none" }} spacing={1}>
                        <StockAlertPanel data={alertData} count={alertCount} compact />
                        <IconButton
                            icon={colorMode}
                            size="sm"
                            variant="ghost"
                            colorScheme="gray"
                            label={colorMode === "dark" ? "Modo claro" : "Modo escuro"}
                            onClick={toggleColorMode}
                        />
                        <IconButton
                            size="sm"
                            variant="ghost"
                            label="Sair"
                            icon="logout"
                            colorScheme="red"
                            isLoading={loading}
                            onClick={handleLogout}
                        />
                    </HStack>

                    {/* Clock — desktop only */}
                    <Text display={{ base: "none", md: "block" }} fontSize="sm" fontWeight="medium" color={timeMuted} fontVariantNumeric="tabular-nums" flexShrink={0}>
                        {timeStr}
                    </Text>

                    {/* Notification + dark mode — desktop only */}
                    <HStack display={{ base: "none", md: "flex" }} spacing={1}>
                        <IconButton
                            icon="notification"
                            size="sm"
                            variant={optedIn ? "solid" : "ghost"}
                            colorScheme={optedIn ? "teal" : undefined}
                            label={optedIn ? "Notificações ativadas" : "Notificações desativadas"}
                            onClick={notification.onOpen}
                        />
                        <IconButton
                            icon={colorMode}
                            size="sm"
                            variant={colorMode === "dark" ? "solid" : "ghost"}
                            colorScheme="gray"
                            label={colorMode === "dark" ? "Modo claro" : "Modo escuro"}
                            onClick={toggleColorMode}
                        />
                    </HStack>

                    <Divider display={{ base: "none", md: "block" }} orientation="vertical" h="28px" />

                    {/* Alertas de estoque — desktop only */}
                    <Box display={{ base: "none", md: "block" }}>
                        <StockAlertPanel data={alertData} count={alertCount} />
                    </Box>

                    {/* User section — desktop only */}
                    <HStack
                        display={{ base: "none", md: "flex" }}
                        spacing={2}
                        bg={userBg}
                        px={3}
                        py={1.5}
                        borderRadius="lg"
                        border="1px solid"
                        borderColor={borderColor}
                        flexShrink={0}
                    >
                        <Avatar size="xs" src={user.data?.avatar_url} name={user.data?.name} />
                        <Box lineHeight={1.2}>
                            <Text fontSize="xs" fontWeight="semibold" textTransform="capitalize" noOfLines={1} maxW="110px">
                                {user.data?.name?.split(" ").slice(0, 2).join(" ")}
                            </Text>
                            {roleLabel && (
                                <Text fontSize="2xs" color={timeMuted} noOfLines={1}>
                                    {roleLabel}
                                </Text>
                            )}
                        </Box>
                        <IconButton
                            size="xs"
                            variant="ghost"
                            label="Sair"
                            icon="logout"
                            colorScheme="red"
                            isLoading={loading}
                            onClick={handleLogout}
                        />
                    </HStack>
                </Flex>
            </Box>

            {/* ── Sidebar ───────────────────────────────────────────────────── */}
            <Flex
                as="nav"
                direction="column"
                position="fixed"
                top={TOPBAR_H}
                left={0}
                w={SIDEBAR_W}
                h={`calc(100vh - ${TOPBAR_H})`}
                zIndex={10}
                bg={barBg}
                borderRightWidth={1}
                borderRightColor={borderColor}
                display={{ base: "none", md: "flex" }}
            >
                {/* Scrollable nav sections */}
                <Box flex={1} overflowY="auto" py={2}>
                    {NAV_SECTIONS.map((section) => {
                        const visibleItems = section.items.filter(item => hasRole(item.role))
                        if (!visibleItems.length) return null
                        return (
                            <Box key={section.label ?? "__main__"} mb={1}>
                                {section.label && (
                                    <Text
                                        px={4}
                                        pt={4}
                                        pb={1}
                                        fontSize="2xs"
                                        fontWeight="bold"
                                        textTransform="uppercase"
                                        letterSpacing="widest"
                                        color={sectionColor}
                                    >
                                        {section.label}
                                    </Text>
                                )}
                                <Stack spacing={0.5}>
                                    {visibleItems.map(({ path, icon, label }) => (
                                        <NavItem
                                            key={path}
                                            icon={icon}
                                            label={label}
                                            active={isActive(path)}
                                            onClick={() => navigate(path)}
                                        />
                                    ))}
                                </Stack>
                            </Box>
                        )
                    })}
                </Box>

                {/* User footer */}
                <Box
                    borderTopWidth={1}
                    borderTopColor={borderColor}
                    px={4}
                    py={3}
                    flexShrink={0}
                >
                    <HStack spacing={2}>
                        <Avatar size="sm" src={user.data?.avatar_url} name={user.data?.name} />
                        <Box minW={0} flex={1}>
                            <Text fontSize="xs" fontWeight="semibold" textTransform="capitalize" noOfLines={1}>
                                {user.data?.name?.split(" ").slice(0, 2).join(" ")}
                            </Text>
                            {roleLabel && (
                                <Tag size="sm" colorScheme="cyan" variant="subtle" mt="2px" fontSize="2xs" h="16px">
                                    {roleLabel}
                                </Tag>
                            )}
                        </Box>
                    </HStack>
                </Box>
            </Flex>

            {/* ── Mobile menu drawer ────────────────────────────────────── */}
            <Drawer isOpen={mobileMenu.isOpen} onClose={mobileMenu.onClose} placement="left" size="xs">
                <DrawerOverlay />
                <DrawerContent bg={barBg}>
                    <DrawerHeader borderBottomWidth={1} borderBottomColor={borderColor} py={3}>
                        <HStack spacing={2}>
                            <Image src="/icon.png" h="24px" />
                            <Text fontWeight="bold" fontSize="sm" color={logoColor} letterSpacing="tight">
                                Bonífica
                            </Text>
                        </HStack>
                        <DrawerCloseButton top={3} />
                    </DrawerHeader>
                    <DrawerBody p={0} overflowY="auto" display="flex" flexDirection="column">
                        <Box py={2} flex={1}>
                            {NAV_SECTIONS.map((section) => {
                                const visibleItems = section.items.filter(item => hasRole(item.role))
                                if (!visibleItems.length) return null
                                return (
                                    <Box key={section.label ?? "__main__"} mb={1}>
                                        {section.label && (
                                            <Text
                                                px={4}
                                                pt={4}
                                                pb={1}
                                                fontSize="2xs"
                                                fontWeight="bold"
                                                textTransform="uppercase"
                                                letterSpacing="widest"
                                                color={sectionColor}
                                            >
                                                {section.label}
                                            </Text>
                                        )}
                                        <Stack spacing={0.5}>
                                            {visibleItems.map(({ path, icon, label }) => (
                                                <NavItem
                                                    key={path}
                                                    icon={icon}
                                                    label={label}
                                                    active={isActive(path)}
                                                    onClick={() => { navigate(path); mobileMenu.onClose() }}
                                                />
                                            ))}
                                        </Stack>
                                    </Box>
                                )
                            })}
                        </Box>

                        {/* User footer no drawer mobile */}
                        <Box
                            borderTopWidth={1}
                            borderTopColor={borderColor}
                            px={4}
                            py={3}
                            flexShrink={0}
                        >
                            <HStack spacing={2} mb={3}>
                                <Avatar size="sm" src={user.data?.avatar_url} name={user.data?.name} />
                                <Box minW={0} flex={1}>
                                    <Text fontSize="xs" fontWeight="semibold" textTransform="capitalize" noOfLines={1}>
                                        {user.data?.name?.split(" ").slice(0, 2).join(" ")}
                                    </Text>
                                    {roleLabel && (
                                        <Tag size="sm" colorScheme="cyan" variant="subtle" mt="2px" fontSize="2xs" h="16px">
                                            {roleLabel}
                                        </Tag>
                                    )}
                                </Box>
                            </HStack>
                            <HStack spacing={2} justify="space-between">
                                <IconButton
                                    icon="notification"
                                    size="sm"
                                    variant={optedIn ? "solid" : "ghost"}
                                    colorScheme={optedIn ? "teal" : undefined}
                                    label={optedIn ? "Notificações ativadas" : "Notificações desativadas"}
                                    onClick={() => { notification.onOpen(); mobileMenu.onClose() }}
                                />
                                <IconButton
                                    size="sm"
                                    variant="ghost"
                                    label="Sair"
                                    icon="logout"
                                    colorScheme="red"
                                    isLoading={loading}
                                    onClick={handleLogout}
                                />
                            </HStack>
                        </Box>
                    </DrawerBody>
                </DrawerContent>
            </Drawer>

            {/* Screens */}
            <Search {...search} />
            <Notification {...notification} />
        </>
    )
}
